import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Calendar } from "lucide-react";
import { lineReveal } from "@/lib/motion";

const HERO_IMG =
  "https://customer-assets-39nsmqrw.emergentagent.net/job_58b37413-1479-4a3f-927c-0029e3907f9e/artifacts/qq4ww6wi_Advertisement_Tasha.jpg";

const GoldSeal = () => (
  <div className="relative h-28 w-28 md:h-32 md:w-32" data-testid="hero-seal">
    <svg viewBox="0 0 200 200" className="absolute inset-0 h-full w-full animate-spin-slow">
      <defs>
        <path id="circlePath" d="M 100,100 m -74,0 a 74,74 0 1,1 148,0 a 74,74 0 1,1 -148,0" />
      </defs>
      <text fill="#C9A24B" fontSize="15" letterSpacing="4" fontFamily="Outfit, sans-serif">
        <textPath href="#circlePath" startOffset="0%">
          FREE TO ATTEND · LIVE ON ZOOM · #ASKTASHA ·
        </textPath>
      </text>
    </svg>
    <div className="absolute inset-0 flex items-center justify-center">
      <div className="flex h-16 w-16 flex-col items-center justify-center rounded-full border border-gold/50 bg-gold/10 md:h-20 md:w-20">
        <span className="font-serif text-2xl font-semibold text-gold md:text-3xl">FREE</span>
      </div>
    </div>
  </div>
);

const Line = ({ children, i, className = "" }) => (
  <span className="hero-line-mask">
    <motion.span variants={lineReveal} custom={i} className={`block ${className}`}>
      {children}
    </motion.span>
  </span>
);

export const Hero = ({ onRegister }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0.2]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen w-full overflow-hidden bg-ink"
    >
      {/* Parallax portrait */}
      <motion.div style={{ y, opacity }} className="absolute inset-0 z-0">
        <div
          className="absolute inset-0 bg-cover bg-[position:30%_center] md:bg-[center_top_-2rem]"
          style={{
            backgroundImage: `url(${HERO_IMG})`,
            backgroundSize: "cover",
          }}
        />
        {/* Gradient masks for text legibility */}
        <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/80 to-ink/10 md:to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/40 to-ink/20" />
      </motion.div>

      {/* Content */}
      <div className="container-tasha relative z-10 flex min-h-screen flex-col justify-center pt-24 pb-16">
        <motion.div
          initial="hidden"
          animate="visible"
          transition={{ staggerChildren: 0.14 }}
          className="max-w-3xl"
        >
          <motion.span
            variants={{ hidden: { opacity: 0 }, visible: { opacity: 1, transition: { delay: 0.1, duration: 0.8 } } }}
            className="overline mb-6 flex items-center gap-3"
          >
            <span className="h-px w-10 bg-gold" /> Free Webinar Class Series
          </motion.span>

          <h1 className="font-serif text-5xl font-semibold leading-[0.95] tracking-tight text-cream sm:text-6xl lg:text-8xl">
            <Line i={0}>Home Health</Line>
            <Line i={1}>Care Agency</Line>
            <Line i={2} className="font-script text-gold pt-2 text-4xl sm:text-6xl lg:text-8xl">
              Class Series
            </Line>
          </h1>

          <motion.p
            variants={{ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0, transition: { delay: 0.8, duration: 0.8 } } }}
            className="mt-8 max-w-xl font-sans text-lg font-light leading-relaxed text-cream/75"
          >
            Your roadmap to launching, growing, and succeeding in home health —
            no clinical background required. Hosted live by Natasha Mays.
          </motion.p>

          <motion.div
            variants={{ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0, transition: { delay: 1, duration: 0.8 } } }}
            className="mt-10 flex flex-col items-start gap-6 sm:flex-row sm:items-center"
          >
            <button onClick={onRegister} data-testid="hero-register-btn" className="btn-gold">
              Save Your Seat
            </button>
            <div className="flex items-center gap-3 font-sans text-sm text-cream/70">
              <Calendar size={18} className="text-gold" />
              <span className="uppercase tracking-widest">Thursdays · 7PM EST</span>
            </div>
          </motion.div>
        </motion.div>

        {/* Seal + scroll cue */}
        <motion.div
          initial={{ opacity: 0, scale: 0.6 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 1.2, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="absolute bottom-24 right-6 hidden md:block md:right-12"
        >
          <GoldSeal />
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6, duration: 1 }}
          className="absolute bottom-8 left-1/2 flex -translate-x-1/2 flex-col items-center gap-2 text-cream/50"
        >
          <span className="text-[10px] uppercase tracking-[0.3em]">Scroll</span>
          <motion.span animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
            <ArrowDown size={16} />
          </motion.span>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
